/**
 * Skill-name matching helpers shared by the skill pickers (#1061).
 *
 * The normalisation mirrors the backend's (backend#992): lower-case, strip
 * diacritics, drop everything except letters, digits, `#` and `+`.
 * "Dynamics 365", "Dynamics365" and "dynamics-365" therefore compare equal,
 * while "C#", "C++" and "C" stay distinct. The server does the
 * real matching; these helpers only decide what to highlight and whether the
 * typed text already names an existing skill.
 */

const normalizeChar = (char: string): string =>
  char
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}#+]/gu, "");

export const normalizeSkillText = (text: string): string =>
  Array.from(text).map(normalizeChar).join("");

/**
 * Where `query` occurs in `text`, ignoring case, accents, spaces and
 * punctuation. Returns the range in the original `text` (end exclusive), or
 * null when the normalised query does not occur.
 */
export const findMatchRange = (text: string, query: string): [number, number] | null => {
  const needle = normalizeSkillText(query);
  if (!needle) {
    return null;
  }
  // Build the normalised text and remember, for every normalised character,
  // the span of the original character that produced it.
  let normalized = "";
  const starts: number[] = [];
  const ends: number[] = [];
  let offset = 0;
  for (const char of Array.from(text)) {
    const n = normalizeChar(char);
    for (let i = 0; i < n.length; i++) {
      starts.push(offset);
      ends.push(offset + char.length);
    }
    normalized += n;
    offset += char.length;
  }
  const at = normalized.indexOf(needle);
  return at < 0 ? null : [starts[at], ends[at + needle.length - 1]];
};
